import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
	Button,
	Divider,
	IconButton,
	InputBase,
	List,
	ListItem,
	ListItemButton,
	Tooltip,
	Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import React, { useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/auth';
import { useConfigContext } from '../../context/config';
import KeyboardShortcut from '../KeyboardShortcut';
import {
	SEARCH__BUTTON_OPEN_SEARCH_KEYBOARD_SHORTCUT,
	SEARCH__BUTTON_TEXT,
	SEARCH___INPUT_CLOSE,
	SEARCH___INPUT_CLOSE_KEYBOARD_SHORTCUT,
	SEARCH___INPUT_PLACEHOLDER,
	SEARCH___RESULTS_NO_RESULTS,
} from './constants';
import { useSearchQuery } from './queries';
import SearchCard from './SearchCard';
import SearchCardSkeleton from './SearchCardSkeleton';
import {
	modalStyles,
	searchButtonStyles,
	searchInputStyles,
	searchResultsStyles,
	SEARCH_BOX__SEARCH_RESULT_HEIGHT,
} from './styles';
import useDebounce, { getSearchResultData, useArrowUpOrDown } from './util';

const Search = () => {
	const navigate = useNavigate();
	const configData = useConfigContext();
	const authData = useAuthContext();
	const { hasPermission } = authData;
	const searchInputRef = useRef(null);
	const searchResultRefs = useRef([]);
	const [focusedListItemIndex, setFocusedListItemIndex] = useState(-1);
	const [open, setOpen] = React.useState(false);
	const [searchQuery, setSearchQuery] = React.useState('');
	const debouncedSearch = useDebounce(searchQuery, 200);
	const { auth } = authData;
	const { authorizationToken: authToken } = auth;
	const { data: searchResults, isLoading } = useSearchQuery(
		debouncedSearch,
		authToken,
	);

	const filteredSearchResults = searchResults?.filter((result) => {
		const parsedResult = getSearchResultData(result, configData, authData);
		if (
			parsedResult?.type === 'policyholder' &&
			!hasPermission('policyholder.fetch')
		) {
			return false;
		}
		if (
			result.entityType === 'entity.policy2' &&
			!hasPermission('policy.fetch')
		) {
			return false;
		}

		if (
			result.entityType === 'entity.claim3' &&
			(!hasPermission('policy.fetch') || !hasPermission('claim.fetch'))
		) {
			return false;
		}

		if (
			parsedResult?.type === 'quote' &&
			(!hasPermission('policy.fetch') || !hasPermission('quote.fetch'))
		) {
			return false;
		}

		if (
			parsedResult?.title2 === 'Application In Progress' &&
			(!hasPermission('policy.fetch') || !hasPermission('quote.fetch'))
		) {
			return false;
		}

		return true;
	});

	const handleOpen = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
	};

	const handleClick = (to: string) => {
		handleClose();
		navigate(to);
	};

	const handleSearch = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		setSearchQuery(e.target.value);
	};

	const selectPrevListItem = (e: any) => {
		if (focusedListItemIndex > 0) {
			searchResultRefs.current[focusedListItemIndex - 1].focus();
			setFocusedListItemIndex(focusedListItemIndex - 1);
		}
		// Focusing on search bar
		if (focusedListItemIndex === 0) {
			searchInputRef.current.focus();
			setFocusedListItemIndex(-1);
		}
	};

	const selectNextListItem = (e: any) => {
		if (focusedListItemIndex < searchResultRefs.current.length - 1) {
			searchResultRefs.current[focusedListItemIndex + 1].focus();
			setFocusedListItemIndex(focusedListItemIndex + 1);
		}
	};

	useHotkeys('/', (e) => {
		e.preventDefault();
		handleOpen();
	});
	useHotkeys('esc', handleClose);
	useArrowUpOrDown(selectPrevListItem, selectNextListItem);

	return (
		<>
			<Tooltip
				title={
					<KeyboardShortcut
						title='Search'
						shortcut={[SEARCH__BUTTON_OPEN_SEARCH_KEYBOARD_SHORTCUT]}
					/>
				}
			>
				<Button
					onClick={handleOpen}
					sx={searchButtonStyles}
					id='Button__open-search'
				>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<SearchIcon />
						<Typography sx={{ ml: 2, color: 'black.lighter' }}>
							{SEARCH__BUTTON_TEXT}
						</Typography>
					</Box>
				</Button>
			</Tooltip>
			<Modal open={open} onClose={handleClose}>
				<Box sx={modalStyles}>
					<Box component='header' sx={searchInputStyles}>
						<SearchIcon />
						<InputBase
							autoFocus
							placeholder={SEARCH___INPUT_PLACEHOLDER}
							sx={{ mr: 1, ml: 1, flex: 1 }}
							onChange={handleSearch}
							onFocus={(event) => {
								event.target.select();
							}}
							value={searchQuery}
							inputRef={searchInputRef}
							id='Search__search-input'
						/>
						<Tooltip
							title={
								<KeyboardShortcut
									shortcut={[SEARCH___INPUT_CLOSE_KEYBOARD_SHORTCUT]}
									title={SEARCH___INPUT_CLOSE}
								/>
							}
						>
							<IconButton onClick={handleClose}>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					</Box>
					<Divider />
					<List role='listbox' sx={searchResultsStyles}>
						{filteredSearchResults &&
							!isLoading &&
							filteredSearchResults.map((result, index) => {
								const searchCardProps = getSearchResultData(
									result,
									configData,
									authData,
								);

								return (
									<React.Fragment key={index}>
										<Divider />
										<ListItem disableGutters disablePadding>
											<ListItemButton
												ref={(el) => (searchResultRefs.current[index] = el)}
												sx={{ height: SEARCH_BOX__SEARCH_RESULT_HEIGHT }}
												onClick={() => handleClick(searchCardProps.value)}
												id={`Search__search-result-${index}`}
											>
												<SearchCard {...searchCardProps} />
											</ListItemButton>
										</ListItem>
									</React.Fragment>
								);
							})}
						{!isLoading && filteredSearchResults?.length === 0 && (
							<>
								<Divider />
								<ListItem disableGutters disablePadding>
									<ListItemButton
										sx={{ height: SEARCH_BOX__SEARCH_RESULT_HEIGHT }}
										disabled
									>
										{SEARCH___RESULTS_NO_RESULTS}
									</ListItemButton>
								</ListItem>
							</>
						)}
						{isLoading &&
							Array(5)
								.fill(0)
								.map((_, index) => (
									<React.Fragment key={index}>
										<Divider />
										<ListItem disableGutters disablePadding>
											<ListItemButton
												sx={{ height: SEARCH_BOX__SEARCH_RESULT_HEIGHT }}
												disabled
											>
												<SearchCardSkeleton />
											</ListItemButton>
										</ListItem>
									</React.Fragment>
								))}
					</List>
				</Box>
			</Modal>
		</>
	);
};

export default Search;
